/// <reference path="../../typings/browser.d.ts" />

namespace Application.Controllers {
    'use strict';

    import IUser = Models.IUser;
    import User = Models.User;

    /**
     * DashboardController class
     */
    export class AdminUsersController {
        public static $name: string = "adminUsersController";

        public static $inject: string[] = ['userService', 'ModalService', '$scope', '$timeout', 'vnoService'];
        public users: IUser[];
        public showDisabled: boolean;
        public matchedUsers: IUser[];
        public vnos: Models.Vno[];

        private userService: Services.UserService;
        private modalService: ngModal.IModalService;
        private $scope: ng.IScope;
        private $timeout: ng.ITimeoutService;
        private vnoService: Services.VnoService;

        private searchText: string;
        private timeoutSearch: ng.IPromise<void>;

        /**
         * Constructor
         */
        constructor(userService: Services.UserService,
                    modalService: ngModal.IModalService,
                    $scope: ng.IScope,
                    $timeout: ng.ITimeoutService,
                    vnoService: Services.VnoService,
                ) {
            this.userService = userService;
            this.modalService = modalService;
            this.$scope = $scope;
            this.$timeout = $timeout;
            this.vnoService = vnoService;
            this.showDisabled = false;
            this.searchText = "";
            this.getAllUsers();
            this.$scope.$watch('auc.showDisabled', (newValue: boolean, oldValue: boolean) => {
                if (newValue !== oldValue) {
                    this.getAllUsers();
                    this.applyFilter();
                }
            });
        }

        public saveUser(user: IUser): ng.IPromise<IUser> {
            return this.userService.saveUser(user)
                .then((response: IUser) => {
                    this.getAllUsers();
                    if (this.searchText !== undefined && this.searchText !== "") {
                        this.applyFilter();
                    }
                    return response; 
                }).catch((reason: ngCustom.IReason) => {
                    return this.modalService.showModal({
                        templateUrl: Controllers.ErrorModalController.templateURL,
                        controller: Controllers.ErrorModalController.$name,
                        inputs: {
                            title: 'Error saving user',
                            description: reason.data.message,
                        },
                    }).then((modal: ngModal.IModalObject) => {
                        let modalElem: UIkit.ModalElement = UIkit.modal(Controllers.ErrorModalController.ID,
                            {bgclose: false, modal: false, keyboard: false});
                        modalElem.show();
                        modal.close.then((result: Object) => {
                            modalElem.hide();
                        });
                        return undefined;
                    });
                });
        }

        public launchUserModal(): void {
            let emptyUser: IUser = User.createEmpty();
            this.vnoService.findAll().then((allVnos: Models.Vno[]) => {
                this.vnos = allVnos;
                this.modalService.showModal({
                    templateUrl: Application.Controllers.UserNewEditModalController.templateURL,
                    controller: Application.Controllers.UserNewEditModalController.$name,
                    inputs: {
                        title: 'Create user',
                        user: emptyUser,
                        vnos: this.vnos,
                    },
                }).then((modal: ngModal.IModalObject) => {
                    let modalElem: UIkit.ModalElement = UIkit.modal(Application.Controllers.UserNewEditModalController.ID,
                                                        {bgclose: false, modal: false, keyboard: false});
                    modalElem.show();
                    modal.close.then((result?: IUserEditResponse) => {
                        if (result) {
                            // with the way the modal is implemented, an existing vno should be selected
                            this.createOrUpdateUser(result.user, result.selectedVno.vno);
                        }
                        modalElem.hide();
                    });
                });
            });
        }

        public toggleUserEnabled(id: number): void {
            let userToToggle: IUser;
            [userToToggle] = this.users.filter(
                (user: IUser) => {
                    return user.id === id;
                });
            if (userToToggle) {
                userToToggle.enabled = !userToToggle.enabled;
                this.saveUser(userToToggle).then(() => {
                    if (!userToToggle.enabled && !this.showDisabled) {
                        Common.Utils.alertOnSuccess('User disabled. Switch to \'Show disabled\' to show it', 'top-right');
                    }
                });
                
            }
        }

        public toggleImpersonate(user: string): void {
            this.impersonateUser(user);
        }

        public impersonateUser(user: string): void {
            this.userService.impersonateUser(user).catch((reason: ngCustom.IReason) => {
                this.modalService.showModal({
                    templateUrl: Controllers.ErrorModalController.templateURL,
                    controller: Controllers.ErrorModalController.$name,
                    inputs: {
                        title: 'Error impersonating user',
                        description: reason.data.message,
                    },
                }).then((modal: ngModal.IModalObject) => {
                    let modalElem: UIkit.ModalElement = UIkit.modal(Controllers.ErrorModalController.ID,
                        {bgclose: false, modal: false, keyboard: false});
                    modalElem.show();
                    modal.close.then((result: Object) => {
                        modalElem.hide();
                    });
                });
            });
        }

        public showImpersonate(user: User): boolean {
            let currentUser = this.userService.getCurrentUser();
            return user.enabled && currentUser.username !== user.username;
        }

        public exportUsers(): void {
            Common.Utils.alertOnInfo('Coming soon!');
        }

        public searchAsType(): void {
            if (this.searchText !== undefined && this.searchText !== "") {
                this.$timeout.cancel(this.timeoutSearch);
                this.timeoutSearch = this.$timeout(() => {
                    this.applyFilter();
                }, 500);
            } else {
                this.matchedUsers = this.users;
            }
        }

        public applyFilter(): void {
            this.matchedUsers = this.users;
            this.matchedUsers = this.users.filter((user: IUser) => {
                    return this.filterUser(user, this.searchText);
                });
        }

        public filterUser(user: IUser, fiterText: string): boolean {
            return (user.username.toLocaleLowerCase().indexOf(fiterText.toLocaleLowerCase()) !== -1 || 
                    user.fullname.toLocaleLowerCase().indexOf(fiterText.toLocaleLowerCase()) !== -1 ||
                    user.email.toLocaleLowerCase().indexOf(fiterText.toLocaleLowerCase()) !== -1 );
        }

        private getAllUsers(): void {         
            let userResult: ng.IPromise<IUser[]>;
            if (this.showDisabled)
                userResult = this.userService.findAllIncludingDisabled();
            else
                userResult = this.userService.findAll();
            userResult.then((response: Models.IUser[]) => {
                this.users = response;
                if (this.searchText !== undefined && this.searchText !== "") {
                    this.applyFilter();
                } else {
                    this.matchedUsers = response;
                }
            });
        }

        private createOrUpdateUser(user: Models.IUser, vno: Models.IVno): void {
            if (user.id) {
                // Update existing user
                this.userService.updateUser(user).then(() => {
                    if (!user.enabled && !this.showDisabled) {
                        Common.Utils.alertOnSuccess('User disabled. Switch to \'Show disabled\' to show it', 'top-right');
                    }
                });
            } else {
                // Create new user
                this.vnoService.createUserIntoVno(user, vno)
                .then(() => {
                    this.getAllUsers();
                }).catch((reason: ngCustom.IReason) => {
                    return this.modalService.showModal({
                        templateUrl: Controllers.ErrorModalController.templateURL,
                        controller: Controllers.ErrorModalController.$name,
                        inputs: {
                            title: 'Error',
                            description: 'Error saving user',
                        },
                    }).then((modal: ngModal.IModalObject) => {
                        let modalElem: UIkit.ModalElement = UIkit.modal(Controllers.ErrorModalController.ID,
                            {bgclose: false, modal: false, keyboard: false});
                        modalElem.show();
                        modal.close.then((result: Object) => {
                            modalElem.hide();
                        });
                        return undefined;
                    });
                });
            }
        }

    }
}
